/**
 * ModelDefaultsBar — the white model-defaults strip that sits directly under the
 * AI Styles header, rebuilt against QUILL's model control plane.
 *
 * Element for element this is the LifeScribe original
 * (components/ai/styles/ModelDefaultsBar.tsx): three labelled Selects — Writing
 * model, Illustration model, Video model — each showing model id + tier chip +
 * cost, each followed by a source chip, an Undo icon-button and an edit-pricing
 * icon-button; then a catalogue-freshness dot and a rate-limited Refresh button.
 *
 * WHAT IS WIRED, AND WHAT IS DISABLED AND WHY. Nothing here renders as
 * enabled-but-inert, and nothing claims an effect it does not have.
 *
 *  • Writing model — LIVE, bound to the `conversation_main` pipeline's ACTIVE
 *    model (PUT /pipeline-models/conversation_main). NOT to the global
 *    `quill_model_defaults.text` slot: that slot still exists and the AI Models
 *    page still edits it, but since migration 528 nothing resolves through it
 *    (quill-engine's ModelResolver.resolve() has no remaining call site;
 *    quill-prosody states it does not consult the table), so binding here would
 *    be a control that writes an audit row and changes nothing.
 *  • Illustration model — DISABLED. Quill has no image-generation model at all.
 *    Its only image models are the three vision pipelines (image_caption,
 *    photo_activity, photo_face_assist), which read a member's photo rather than
 *    draw anything, and they are assigned per pipeline on Pipeline Models.
 *  • Video model — DISABLED. Quill has no video pipeline and no video model in
 *    the catalogue. There is nothing for this to set.
 *  • Edit pricing — DISABLED. `pricing_amount` / `pricing_unit` are read-only
 *    columns on `quill_xai_models`, written only by the catalogue sync; the
 *    admin backend exposes no pricing write path at all. Spend lives on Costs.
 *  • Undo — LIVE for the writing slot, and (as in the original) enabled only
 *    when the source is `db`: a pipeline still on its seeded model has no prior
 *    value in the change log, so revert would 400 NO_PRIOR_VALUE.
 *  • Refresh — LIVE. POST /models/refresh, server-side rate limit of 1 per 30s
 *    per admin; the button mirrors that with a local cooldown.
 *
 * The one deliberate addition to the original's behaviour: changing or reverting
 * the model is a production write, so it routes through QuillActionConfirm like
 * every other write in the Quill admin, instead of firing on select.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import UndoIcon from '@mui/icons-material/Undo';
import EditIcon from '@mui/icons-material/Edit';

import QuillActionConfirm from '../common/QuillActionConfirm';
import { ModelCatalogRow, QuillApiError } from '../../../services/quillAdminService';
import { PRODUCTION_CAPTION } from './constants';
import { ModelSource, WRITING_PIPELINE_KEY, formatCost, useModelRegistry } from './useModelRegistry';

/** The three strip positions, in the LifeScribe original's order. */
export type StripSlot = 'text' | 'image' | 'video';

const SLOT_LABELS: Record<StripSlot, string> = {
  text: 'Writing model',
  image: 'Illustration model',
  video: 'Video model',
};

/**
 * `null` reason = the slot is live. A non-null reason is rendered as the
 * control's tooltip and the control is disabled.
 */
const SLOT_UNSUPPORTED: Record<StripSlot, string | null> = {
  text: null,
  image:
    'Quill has no illustration model — nothing in the engine generates images. Its only image models are the vision pipelines (image_caption, photo_activity, photo_face_assist), which read member photos and are set on Pipeline Models.',
  video:
    'Quill has no video pipeline and no video model in the catalogue. There is nothing for this to set.',
};

const WRITING_TOOLTIP =
  'The model the conversation_main pipeline runs — Quill’s main composition call. It does not change how the styles on this page read: LifeScribe’s transformer writes chapter prose, resolving each style by its LifeScribe style key.';

/** `env` / `hardcoded` are kept for parity; Quill's pipeline plane reports neither. */
const SOURCE_COLOR: Record<string, 'success' | 'info' | 'warning' | 'default'> = {
  db: 'success',
  env: 'info',
  hardcoded: 'warning',
  seed: 'warning',
  none: 'default',
};

const SOURCE_TOOLTIP: Record<ModelSource, string> = {
  db: 'Source: an admin set this model on the pipeline — the previous value is in the change log',
  seed: 'Source: the model this pipeline was seeded with — no admin has changed it, so there is nothing to revert to',
  none: 'Source: nothing set — the pipeline row could not be read',
};

const PRICING_DISABLED_TOOLTIP =
  'Model pricing is read-only in Quill — the catalogue sync writes pricing_amount / pricing_unit on quill_xai_models and the admin backend exposes no pricing write path. Actual spend is on Quill → Costs.';

/** Server-side limiter is 1 per 30s per admin; mirror it in the button. */
const REFRESH_COOLDOWN_SECONDS = 30;

/** The backend flags the catalogue stale at 96h. */
const STALE_THRESHOLD_HOURS = 96;

function formatSyncAge(hours: number | null): string {
  if (hours === null || !Number.isFinite(hours)) return 'never';
  if (hours < 1) return `${Math.round(hours * 60)} min ago`;
  if (hours < 48) return `${hours.toFixed(1)} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}

/** Best-effort retry-after, matching quill/models/ModelCatalogTable. */
function parseRetryAfterSeconds(message: string): number | null {
  const match = message.match(/(\d+)\s*s(ec(ond)?s?)?\b/i);
  return match ? parseInt(match[1], 10) : null;
}

type Pending =
  | { kind: 'set'; modelId: string; previous: string | null }
  | { kind: 'revert'; current: string | null };

export interface ModelDefaultsBarProps {
  /** Which positions to render. Default matches the original's AI Styles call. */
  slots?: StripSlot[];
  notify: (message: string, severity?: 'success' | 'error' | 'info' | 'warning') => void;
  /** Rendered as a full-width line inside the strip (the Pipeline Models note). */
  footer?: React.ReactNode;
}

const ModelDefaultsBar: React.FC<ModelDefaultsBarProps> = ({
  slots = ['text', 'image', 'video'],
  notify,
  footer,
}) => {
  const {
    loading,
    updating,
    error,
    lastSyncedAt,
    stale,
    syncAgeHours,
    refreshModels,
    updateWritingModel,
    revertWritingModel,
    writingModels,
    effectiveWritingModel,
  } = useModelRegistry();

  const [pending, setPending] = useState<Pending | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearInterval(timerRef.current);
    },
    []
  );

  const startCooldown = (seconds: number) => {
    setCooldown(seconds);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleRefresh = async () => {
    try {
      const resp = await refreshModels();
      const sync = resp?.sync;
      notify(
        sync
          ? `Synced ${sync.models_found} models (${sync.models_inserted} new, ${sync.models_updated} updated, ${sync.models_deprecated} deprecated).`
          : 'Model catalogue refreshed.',
        'success'
      );
      startCooldown(REFRESH_COOLDOWN_SECONDS);
    } catch (e) {
      if (e instanceof QuillApiError && e.code === 'RATE_LIMITED') {
        const seconds = parseRetryAfterSeconds(e.message) ?? REFRESH_COOLDOWN_SECONDS;
        notify(`Refresh is rate-limited — try again in ${seconds}s.`, 'warning');
        startCooldown(seconds);
      } else {
        notify(e instanceof Error ? e.message : 'Refresh failed.', 'error');
      }
    }
  };

  const runPending = async () => {
    if (!pending) return;
    setSubmitting(true);
    try {
      if (pending.kind === 'set') {
        await updateWritingModel(pending.modelId, reason.trim() || undefined);
        notify(
          `${WRITING_PIPELINE_KEY} now runs ${pending.modelId}. The engine picks it up within 60s.`,
          'success'
        );
      } else {
        await revertWritingModel();
        notify(`${WRITING_PIPELINE_KEY} reverted to its previous model.`, 'success');
      }
      setPending(null);
      setReason('');
    } catch (e) {
      if (e instanceof QuillApiError && e.code === 'NO_PRIOR_VALUE') {
        notify('No prior model recorded for this pipeline — nothing to revert to.', 'info');
      } else {
        notify(e instanceof Error ? e.message : 'The change could not be saved.', 'error');
      }
      setPending(null);
      setReason('');
    } finally {
      setSubmitting(false);
    }
  };

  const writing = effectiveWritingModel();
  const writingOptions = writingModels();

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderBottom: '1px solid #e2e8f0',
        px: 3,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 700, color: '#1e293b', mr: 1 }}
      >
        Quill model defaults
      </Typography>

      {loading && <CircularProgress size={18} aria-label="Loading model registry" />}

      {!loading &&
        slots.map((slot) => {
          const unsupported = SLOT_UNSUPPORTED[slot];
          const live = !unsupported;
          const value = live ? writing.value : null;
          const source: ModelSource = live ? writing.source : 'none';
          const capable = live ? writingOptions : [];
          const selectedModel = capable.find((m: ModelCatalogRow) => m.model_id === value);
          const label = SLOT_LABELS[slot];

          return (
            <Box key={slot} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Tooltip title={unsupported ?? WRITING_TOOLTIP}>
                <span>
                  <FormControl size="small" sx={{ minWidth: 260 }} disabled={updating || !live}>
                    <InputLabel id={`quill-slot-${slot}-label`}>{label}</InputLabel>
                    <Select
                      labelId={`quill-slot-${slot}-label`}
                      id={`quill-slot-${slot}`}
                      label={label}
                      value={value && capable.some((m) => m.model_id === value) ? value : ''}
                      onChange={(e) => {
                        const next = String(e.target.value || '');
                        if (!live || !next || next === value) return;
                        setReason('');
                        setPending({ kind: 'set', modelId: next, previous: value });
                      }}
                    >
                      {capable.map((m: ModelCatalogRow) => (
                        <MenuItem key={m.model_id} value={m.model_id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Typography sx={{ fontSize: '0.85rem', flex: 1 }}>
                              {m.model_id}
                            </Typography>
                            {m.tier && (
                              <Chip
                                size="small"
                                label={m.tier}
                                sx={{ height: 18, fontSize: '0.6rem', textTransform: 'uppercase' }}
                              />
                            )}
                            <Typography
                              sx={{
                                fontSize: '0.7rem',
                                color: '#64748b',
                                minWidth: 70,
                                textAlign: 'right',
                              }}
                            >
                              {formatCost(m)}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </span>
              </Tooltip>

              <Tooltip title={SOURCE_TOOLTIP[source]}>
                <Chip
                  size="small"
                  color={SOURCE_COLOR[source] || 'default'}
                  label={source}
                  sx={{ height: 20, fontSize: '0.6rem', textTransform: 'uppercase' }}
                />
              </Tooltip>

              <Tooltip
                title={
                  !live
                    ? 'Nothing to revert — Quill has no model for this slot.'
                    : source === 'db'
                      ? 'Revert to the previous model'
                      : 'Nothing to revert — no admin has changed this pipeline.'
                }
              >
                <span>
                  <IconButton
                    size="small"
                    aria-label={`Revert ${label}`}
                    onClick={() => setPending({ kind: 'revert', current: value })}
                    disabled={updating || !live || source !== 'db'}
                  >
                    <UndoIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </span>
              </Tooltip>

              {selectedModel && (
                <Tooltip title={PRICING_DISABLED_TOOLTIP}>
                  <span>
                    <IconButton
                      size="small"
                      aria-label={`Edit pricing for ${selectedModel.model_id}`}
                      disabled
                    >
                      <EditIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          );
        })}

      <Box sx={{ flex: 1 }} />

      {error && <Typography sx={{ fontSize: '0.72rem', color: '#b91c1c' }}>{error}</Typography>}

      <Tooltip
        title={
          lastSyncedAt
            ? `${stale ? `Last catalogue sync > ${STALE_THRESHOLD_HOURS}h ago` : 'Synced from the model providers'} — ${formatSyncAge(
                syncAgeHours
              )} (at ${new Date(lastSyncedAt).toLocaleString()})`
            : 'No sync recorded yet — click Refresh'
        }
      >
        <Box
          aria-label={stale ? 'catalog-stale' : 'catalog-fresh'}
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: stale ? '#f59e0b' : lastSyncedAt ? '#10b981' : '#94a3b8',
            flexShrink: 0,
          }}
        />
      </Tooltip>

      <Tooltip
        title={
          cooldown > 0
            ? `Rate-limited — available in ${cooldown}s`
            : 'Sync the Quill model catalogue from xAI and Anthropic (rate-limited: 1 per 30s)'
        }
      >
        <span>
          <Button
            size="small"
            variant="outlined"
            /* The header has its own "Refresh" (reload catalogues); name this one. */
            aria-label="Refresh model catalogue"
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            onClick={handleRefresh}
            disabled={updating || cooldown > 0}
          >
            Refresh
          </Button>
        </span>
      </Tooltip>

      {footer && <Box sx={{ flexBasis: '100%', mt: 1.5 }}>{footer}</Box>}

      {pending && (
        <QuillActionConfirm
          open
          title={pending.kind === 'set' ? 'Change the writing model' : 'Revert the writing model'}
          message={
            pending.kind === 'set' ? (
              <>
                Set the <strong>{WRITING_PIPELINE_KEY}</strong> pipeline to run{' '}
                <strong>{pending.modelId}</strong>
                {pending.previous ? (
                  <>
                    , replacing <strong>{pending.previous}</strong>
                  </>
                ) : null}
                . This is Quill&rsquo;s main composition call, so it changes what every member&rsquo;s
                next reply is written by.
              </>
            ) : (
              <>
                Restore the previous model on the <strong>{WRITING_PIPELINE_KEY}</strong> pipeline
                {pending.current ? (
                  <>
                    , replacing <strong>{pending.current}</strong>
                  </>
                ) : null}
                .
              </>
            )
          }
          detail={
            pending.kind === 'set' ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  It does <strong>not</strong> change how the styles on this page read — LifeScribe&rsquo;s
                  transformer writes chapter prose and resolves each style by its LifeScribe style key.
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  label="Reason (optional)"
                  helperText="Recorded in the pipeline change log next to your admin email."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Revert restores the most recent previous model from the change log, re-validated
                against the catalogue.
              </Typography>
            )
          }
          caption={PRODUCTION_CAPTION}
          confirmLabel={pending.kind === 'set' ? 'Change model' : 'Revert'}
          destructive={pending.kind === 'revert'}
          loading={submitting}
          pendingLabel="Working…"
          onConfirm={runPending}
          onCancel={() => {
            setPending(null);
            setReason('');
          }}
        />
      )}
    </Box>
  );
};

export default ModelDefaultsBar;
